import type { Entry } from "contentful";
import { Link, useSearchParams } from "react-router";

import type { Store } from "~/components/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import type { TypeImageLinkSkeleton } from "~/contentful/compiled";
import { ContentfulImage } from "~/contentful/components/contentful-image";
import { useURL } from "~/contexts";
import { cn } from "~/lib/ui";

import { LoadMore } from "../product-list/components/load-more";
import StoreHours from "./store-hours";

const StoresList = ({
  shops,
  brandLogo,
  setHoveredStoreId,
  mapCenter,
  setSelectedStore,
  selectedStore,
  total,
  coordinates,
}: {
  shops: Store[];
  setSelectedStore: (storeId: string) => void;
  selectedStore: string;
  brandLogo: Entry<TypeImageLinkSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>;
  setHoveredStoreId: (id: string | null) => void;
  mapCenter: {
    lat: number;
    lng: number;
  };
  total: number;
  coordinates: { lat: number; lng: number };
}) => {
  const url = useURL();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");

  return (
    <div className="">
      {shops && shops.length !== 0 && (
        <div className="mt-5 flex w-full flex-col gap-4 overflow-y-auto">
          {shops.map((store, index) => (
            <button
              className={cn(
                "group h-fit w-full justify-start rounded-2xl border border-gray-200 p-4 text-left shadow-sm transition-shadow hover:shadow-md",
                selectedStore === store.id && "border-2 border-accountImage",
              )}
              data-shop-coordinates={`${store.latitude};${store.longitude}`}
              onClick={() => {
                setSelectedStore(store.id);
              }}
              onMouseEnter={() => {
                setHoveredStoreId(store.id);
              }}
              onMouseLeave={() => {
                setHoveredStoreId("");
              }}
              key={store.id}
            >
              <div className="flex w-full flex-col gap-3">
                <div className="flex flex-wrap items-start justify-between gap-x-2">
                  <h4 className="min-w-0 flex-1 break-words text-base font-medium text-gray-900">
                    {store.name}
                  </h4>
                  <ContentfulImage
                    alt={brandLogo.fields.image?.fields.description ?? ""}
                    imageUrl={brandLogo.fields.image?.fields.file?.url ?? ""}
                    className="h-4 w-4 max-w-[50%]"
                    itemProp="image"
                  />
                </div>
                {store.distance && (
                  <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
                    {store.distance} {store.distanceUnit} away
                  </span>
                )}

                {store.c_streetName && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.c_streetName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-fit text-sm text-blue-600 hover:underline"
                  >
                    {store.c_streetName}
                  </a>
                )}
                {store.storeHours && (
                  <Accordion
                    type="single"
                    collapsible
                    className="rounded-none bg-none"
                  >
                    <AccordionItem
                      value="details"
                      className="rounded-none bg-none"
                    >
                      <AccordionTrigger
                        className={
                          "bottom-0 border-none px-0 py-2 data-[state=open]:bg-inherit"
                        }
                      >
                        <span className="border-none text-sm font-normal text-neutral-11">
                          Store Hours
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="[&>div]:p-0 [&>div]:px-0 [&>div]:py-0">
                        <StoreHours storeHours={store.storeHours} />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
                <Link
                  to={url(`/store-locator/store-details/${store.id}`)}
                  state={{ mapCenter, page }}
                >
                  <Button variant="outline">View Store Details</Button>
                </Link>
              </div>
            </button>
          ))}
          {total > 10 && (
            <div className="mb-7">
              <LoadMore coordinates={coordinates}></LoadMore>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoresList;
